import { advanceAfter, type AdvanceInput } from './guide.js';

/**
 * The voice guide's timer (ADR-035; voice-guide design, H4): when a guided
 * step moves on by itself. It belongs to a step entry: a step change, Verder,
 * Terug, "Nog eens", a pause, a switch to locked, an open hotspot and a change
 * of guide or Voorlezen all cancel it, and it is set again from the rules in
 * `advanceAfter` whenever what they read changes. When it fires it checks that
 * its entry is still current, and a Verder pressed in the moment after it has
 * fired is swallowed -- so an auto-advance and a press never move two steps.
 *
 * Its clock is the caller's (`now`, in seconds); nothing here touches the DOM.
 */

/** A Verder pressed this soon after the step moved on by itself landed on the next step by accident. */
export const PRESS_GRACE_SECONDS = 0.4;

export type PacerOptions = {
  now: () => number;
  /** Runs `run` after `seconds`; returns a cancel. */
  schedule: (run: () => void, seconds: number) => () => void;
  /** Moves the lesson on one step (the player's own `next`). */
  advance: () => void;
};

type Entry = Pick<AdvanceInput, 'control' | 'phase' | 'isLast'>;

export class GuidePacer {
  readonly #options: PacerOptions;
  #entry = 0;
  #active = false;
  #facts: AdvanceInput = {
    control: 'free',
    phase: 'panel',
    isLast: false,
    hotspotOpen: false,
    paused: false,
    voiceEnded: undefined,
    changeEnded: 0,
  };
  #cancel: (() => void) | undefined;
  #due: number | undefined;
  #advancedAt = Number.NEGATIVE_INFINITY;
  #disposed = false;

  constructor(options: PacerOptions) {
    this.#options = options;
  }

  /** When the step will move on, on the caller's clock; undefined: not by itself. */
  get due(): number | undefined {
    return this.#due;
  }

  /** Whether the step is guided by the voice (`voice-text` or `voice-subtitles`): only then does it pace. */
  setActive(active: boolean): void {
    if (active === this.#active) return;
    this.#active = active;
    this.#plan();
  }

  /** A new step entry: whatever was timed for the last one is gone. */
  enter(entry: Entry): void {
    this.#entry++;
    this.#facts = {
      ...this.#facts,
      ...entry,
      hotspotOpen: false,
      voiceEnded: undefined,
      changeEnded: 0,
      readEnded: undefined,
      heldUntil: 0,
    };
    this.#plan();
  }

  /** The step's phase, control or place changed within the entry. */
  update(facts: Partial<Entry>): void {
    this.#facts = { ...this.#facts, ...facts };
    this.#plan();
  }

  /** The voice has ended, or an answer cut it. */
  voiceEnded(): void {
    this.#facts = { ...this.#facts, voiceEnded: this.#options.now() };
    this.#plan();
  }

  /** The voice starts again from the start (the guide or Voorlezen switched back): wait for it. */
  restarted(): void {
    this.#facts = { ...this.#facts, voiceEnded: undefined };
    this.#plan();
  }

  /** The step's change runs for `seconds` from now. */
  changeEnds(seconds: number): void {
    this.#facts = { ...this.#facts, changeEnded: this.#options.now() + seconds };
    this.#plan();
  }

  /** An answer's outcome shows, to be read for `seconds`. */
  resultShown(seconds: number): void {
    this.#facts = { ...this.#facts, readEnded: this.#options.now() + seconds };
    this.#plan();
  }

  /** A hotspot's card opened; closed, the full pause runs again. */
  hotspot(open: boolean): void {
    this.#facts = {
      ...this.#facts,
      hotspotOpen: open,
      ...(open ? {} : { heldUntil: this.#options.now() }),
    };
    this.#plan();
  }

  /** The teacher's pause; lifted, the full pause runs again. */
  paused(paused: boolean): void {
    if (paused === this.#facts.paused) return;
    this.#facts = {
      ...this.#facts,
      paused,
      ...(paused ? {} : { heldUntil: this.#options.now() }),
    };
    this.#plan();
  }

  /** "↻ Nog eens": the voice again, and the change for `changeSeconds`. */
  replayed(changeSeconds: number): void {
    const now = this.#options.now();
    this.#facts = {
      ...this.#facts,
      voiceEnded: undefined,
      changeEnded: now + changeSeconds,
      heldUntil: 0,
    };
    this.#plan();
  }

  /**
   * A press of Verder: the timer goes, and the press goes through -- unless
   * the step moved on by itself a moment ago, and the press landed on the
   * next step's Verder by accident.
   */
  pressForward(): boolean {
    this.#clear();
    return this.#options.now() - this.#advancedAt >= PRESS_GRACE_SECONDS;
  }

  dispose(): void {
    this.#disposed = true;
    this.#clear();
  }

  #clear(): void {
    this.#cancel?.();
    this.#cancel = undefined;
    this.#due = undefined;
  }

  #plan(): void {
    this.#clear();
    if (!this.#active || this.#disposed) return;
    const at = advanceAfter(this.#facts);
    if (at === undefined) return;
    const entry = this.#entry;
    const now = this.#options.now();
    this.#due = at;
    const cancel = this.#options.schedule(
      () => {
        if (this.#cancel !== cancel || entry !== this.#entry || this.#disposed) return;
        this.#cancel = undefined;
        this.#due = undefined;
        this.#advancedAt = this.#options.now();
        this.#options.advance();
      },
      Math.max(0, at - now),
    );
    this.#cancel = cancel;
  }
}
